import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import type { InputRef } from 'antd';
import { Table, Input, Button, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType, ColumnType, TablePaginationConfig } from 'antd/es/table';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import './styles.css';

// --- Types ---
type TableWithSearchProps<T> = {
  data: T[];
  columns: ColumnsType<T>;
  searchKeys?: (keyof T)[];
  pagination?: boolean | TablePaginationConfig;
  headerOffset?: number;
  defaultPageSize?: number;
  rowKey?: string | ((record: T) => React.Key);
};

// --- Constants ---
const DEFAULT_HEADER_OFFSET = 350;
const DEFAULT_TABLE_PAGE_SIZE = 50;

// --- Utils ---
const filterTableData = <T extends Record<string, any>>(
  data: T[],
  dataIndex: keyof T,
  searchValue: string,
): T[] => {
  if (!searchValue) return data;
  const q = searchValue.toLowerCase();
  return data.filter((item) =>
    String(item[dataIndex] ?? '')
      .toLowerCase()
      .includes(q),
  );
};

// --- Component ---
const TableWithSearch = <T extends Record<string, any>>({
  data,
  columns,
  searchKeys = [],
  pagination = true,
  headerOffset = DEFAULT_HEADER_OFFSET,
  defaultPageSize = DEFAULT_TABLE_PAGE_SIZE,
  rowKey = 'id',
  ...restProps
}: TableWithSearchProps<T>): JSX.Element => {
  const searchInputRef = useRef<InputRef>(null);

  // Keep latest headerOffset without re-subscribing listeners
  const headerOffsetRef = useRef(headerOffset);
  useEffect(() => {
    headerOffsetRef.current = headerOffset;
  }, [headerOffset]);

  // Width/Height driven states
  const [vw, setVw] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 0));
  const [scrollY, setScrollY] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight - headerOffsetRef.current : 0,
  );

  // Data / search / pagination state
  const [currentFilteredData, setCurrentFilteredData] = useState<T[]>(data);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');

  const [paginationConfig, setPaginationConfig] = useState<TablePaginationConfig>(
    typeof pagination === 'object'
      ? { ...pagination, total: data.length }
      : {
          current: 1,
          pageSize: defaultPageSize,
          total: data.length,
        },
  );

  // Resize listeners (attach once)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let raf = 0;
    const handleResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        // Use clientWidth instead of innerWidth for more accurate sizing on Mac laptops
        const newVw = Math.min(window.innerWidth, window.document.documentElement.clientWidth);
        setVw(newVw); // force Table remount -> re-measure columns
        setScrollY(window.innerHeight - headerOffsetRef.current); // numeric scroll.y
      });
    };

    handleResize(); // set initial values
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // Optional: better on mobile chrome UI chrome changes
    const vv = (window as any).visualViewport as VisualViewport | undefined;
    vv?.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      vv?.removeEventListener('resize', handleResize);
    };
  }, []);

  // Reset when data changes
  useEffect(() => {
    setCurrentFilteredData(data);
    setPaginationConfig((prev) => ({
      ...prev,
      total: data.length,
      current: 1,
    }));
    setSearchText('');
    setSearchedColumn('');
  }, [data]);

  // Search handlers
  const handleSearch = useCallback(
    (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: keyof T) => {
      confirm();
      const currentSearchText = selectedKeys[0] || '';
      setSearchText(currentSearchText);
      setSearchedColumn(dataIndex as string);

      const filtered = filterTableData(data, dataIndex, currentSearchText);
      setCurrentFilteredData(filtered);
      setPaginationConfig((prev) => ({
        ...prev,
        current: 1,
        total: filtered.length,
      }));
    },
    [data],
  );

  const handleReset = useCallback(
    (
      clearFilters: () => void,
      setSelectedKeys: (keys: React.Key[]) => void,
      confirm: () => void,
      close: () => void,
    ) => {
      clearFilters();
      setSelectedKeys([]);
      setSearchText('');
      setSearchedColumn('');
      setCurrentFilteredData(data);
      confirm();
      close();

      setPaginationConfig((prev) => ({
        ...prev,
        current: 1,
        total: data.length,
      }));

      searchInputRef.current?.input?.blur?.();
    },
    [data],
  );

  // Column search props
  const getColumnSearchProps = useCallback(
    (dataIndex: keyof T): ColumnType<T> => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInputRef}
            placeholder={`Search ${String(dataIndex)}`}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            style={{ marginBottom: 8, display: 'block' }}
            allowClear
          />
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => handleReset(clearFilters!, setSelectedKeys, confirm, close)}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => {
                confirm({ closeDropdown: true });
                setSearchText('');
                setSearchedColumn('');
              }}
            >
              Close
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
      ),
      onFilter: (value, record) =>
        String(record[dataIndex] ?? '')
          .toLowerCase()
          .includes(String(value).toLowerCase()),
      onFilterDropdownOpenChange: (visible) => {
        if (visible) setTimeout(() => searchInputRef.current?.select(), 100);
      },
      sorter: (a, b) => {
        const aVal = String(a[dataIndex] ?? '');
        const bVal = String(b[dataIndex] ?? '');
        return aVal.localeCompare(bVal);
      },
    }),
    [handleSearch, handleReset],
  );

  // Enhance only searchable columns; also avoid fixed widths so columns can shrink
  const enhancedColumns = useMemo(() => {
    return columns.map((col) => {
      // Only process columns that have dataIndex (skip group headers)
      if ('dataIndex' in col && col.dataIndex) {
        const isSearchable = searchKeys.includes(col.dataIndex as keyof T);
        const merged = isSearchable
          ? { ...col, ...getColumnSearchProps(col.dataIndex as keyof T) }
          : col;

        // Ensure responsive shrink: prefer no fixed width unless you need it.
        const { width, ...rest } = merged as any;
        return { ...rest, ellipsis: true }; // truncate long text to avoid overflow
      }

      // Return column as-is if it doesn't have dataIndex (like group headers)
      return col;
    });
  }, [columns, searchKeys, getColumnSearchProps]);

  // Pagination change
  const handleTableChange = useCallback((newPagination: TablePaginationConfig) => {
    setPaginationConfig(newPagination);
  }, []);

  return (
    <div className="table-container" style={{ width: '100%', overflow: 'hidden' }}>
      <div style={{ overflow: 'auto', maxWidth: '100%' }}>
        <Table
          key={vw}
          {...restProps}
          columns={enhancedColumns}
          dataSource={currentFilteredData}
          pagination={pagination ? paginationConfig : false}
          onChange={handleTableChange}
          rowKey={rowKey}
          tableLayout="fixed"
          scroll={{ x: 'max-content', y: scrollY }}
          style={{ minWidth: 0, maxWidth: '100%' }}
        />
      </div>
    </div>
  );
};

export default TableWithSearch;
